import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useTable,
  useBlockLayout,
  useSortBy,
  useRowSelect,
  useGlobalFilter,
  useExpanded,
  TableInstance,
} from 'react-table';
import { AutoSizer } from 'react-virtualized';
import {
  Box as Wrapper,
  Icon,
  Space,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Flex,
  Text,
  Checkbox,
  Spinner,
  useColorMode,
  useColorStyle,
} from '@tonic-ui/react';
import styles from './table-panel.module.css';
import LoadingOverlay from 'react-loading-overlay';
import styled from '@emotion/styled';

const IndeterminateCheckbox = forwardRef(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef<HTMLInputElement>();
  const resolvedRef = ref || defaultRef;

  return <Checkbox ref={resolvedRef} indeterminate={indeterminate} {...rest} />;
});

type TableProps = {
  onSelectedRowChange?: () => void;
  calcHeight?: number;
  selectedRow?: { id: string };
  rowSelectionKey: string;
  data: Record<string, unknown>;
  columns: Record<string, unknown>;
  enableMultiSelect?: boolean;
  showSearchFilter?: boolean;
};

type TablePanelRef = TableInstance;

const TableContainer = styled.div`
  @media screen and (max-height: 1024px) {
    height: 58vh;
  }
  @media only screen and (min-height: 1025px) {
    height: 68vh;
  }
`;

const TablePanel = forwardRef<TablePanelRef, TableProps>(
  (
    {
      columns,
      data = [],
      calcHeight,
      onSelectedRowChange = (param: string | Record<string, unknown>) => param,
      rowSelectionKey = 'id',
      selectedRow,
      enableMultiSelect = false,
    },
    ref
  ): JSX.Element => {
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    // dark mode styling
    const colorModeClass = (colorMode === 'dark' ? styles.dark : '') as string;
    const { t } = useTranslation();
    const rowHoverBgColor =
      colorMode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)';
    const rowSelectedBgColour =
      colorMode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)';

    const getCalculatedColumns = ({ initColumns, tableWidth }) => {
      const columns = initColumns.map((column) => {
        let columnWidth = column.width;
        if (typeof columnWidth === 'string') {
          const lastChar = columnWidth.substr(columnWidth.length - 1);
          if (lastChar === '%') {
            columnWidth = tableWidth * (parseFloat(columnWidth) / 100);
            return {
              ...column,
              width: columnWidth,
            };
          }
          if (columnWidth === 'auto') {
            return {
              ...column,
              width: 0,
            };
          }
        }
        return column;
      });
      const customWidthColumns = columns.filter((column) => column.width);
      const totalCustomWidth = customWidthColumns.reduce(
        (accumulator, column) => accumulator + column.width,
        0
      );
      let defaultCellWidth =
        (tableWidth - totalCustomWidth) / (columns.length - customWidthColumns.length);
      defaultCellWidth = defaultCellWidth <= 0 ? 150 : defaultCellWidth;
      return columns.map((column) => {
        if (column.width) {
          return column;
        }
        return {
          ...column,
          width: defaultCellWidth,
        };
      });
    };

    const instance = useTable(
      {
        columns,
        data,
        disableSortRemove: true,
        autoResetSelectedRows: false,
        autoResetExpanded: true,
        initialState: { selectedRowIds: { 0: true } },
        getSubRows: (row: any) => row.subRows,
      },
      useGlobalFilter,
      useBlockLayout,
      useSortBy,
      useExpanded,
      useRowSelect,
      (hooks) => {
        if (enableMultiSelect) {
          hooks.visibleColumns.push((columns) => [
            {
              id: 'selection',
              Header: ({ getToggleAllRowsSelectedProps }) => (
                <Flex height="100%" alignItems="center">
                  <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
                </Flex>
              ),
              Cell: ({ row }) => (
                <Flex height="100%" alignItems="center">
                  <IndeterminateCheckbox
                    {...row.getToggleRowSelectedProps()}
                    disabled={row.original.disableCheckboxSelection}
                  />
                </Flex>
              ),
              width: 50,
            },
            ...columns.slice(1),
          ]);
          // overriding getToggleAllRowsSelectedProps to not select disabled rows on (select all/deselect all) operation
          hooks.getToggleAllRowsSelectedProps = [
            (props, { instance }) => [
              props,
              {
                onChange: () => {
                  //TODO: investigate why e.target.checked is always true, to simplify select/deselect logic below
                  instance.rows.forEach((row) => {
                    row.toggleRowSelected(
                      row.original.disableCheckboxSelection
                        ? false // unselect row with disabled checkbox
                        : !instance.rows
                            .filter((row) => !row.original.disableCheckboxSelection)
                            .every((row) => row.isSelected) //toggle based on state of all the rows without disabled checkboxes
                    );
                  });
                },
                style: { cursor: 'pointer' },
                checked: instance.rows.every((row) => row.isSelected),
                title: 'Toggle All Rows Selected',
                indeterminate: Boolean(
                  !instance.isAllRowsSelected && Object.keys(instance.state.selectedRowIds).length
                ),
              },
            ],
          ];
        }
      }
    );

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      rows,
      prepareRow,
      selectedFlatRows,
      state: { selectedRowIds, globalFilter, expanded },
      toggleAllRowsSelected,
      setGlobalFilter,
    } = instance;

    useImperativeHandle(ref, () => instance);

    // handling multiple rows selection
    useEffect(() => {
      if (enableMultiSelect) {
        onSelectedRowChange(selectedFlatRows.map((r) => r.original));
      }
    }, [enableMultiSelect, onSelectedRowChange, selectedFlatRows]);

    // handling single row selection
    const onCellClick = (row) => {
      if (enableMultiSelect) {
        toggleAllRowsSelected(false);
        row.toggleRowSelected(true);
      } else if (row.canExpand && !row.isExpanded) {
        row.toggleRowExpanded(true);
        row.toggleRowSelected(row.id, true);
        onSelectedRowChange(row.subRows[0].original);
      } else if (row.isExpanded) {
        row.toggleRowExpanded(false);
      } else {
        row.toggleRowSelected(row.id, true);
        onSelectedRowChange(row.original);
      }
    };

    return (
      <TableContainer>
        <AutoSizer>
          {({ height, width }) => {
            if (height === 0 || width === 0) {
              return null;
            }
            const newColumns = getCalculatedColumns({ initColumns: columns, tableWidth: width });
            return (
              <>
                <Table {...getTableProps()}>
                  <TableHeader>
                    {headerGroups.map((headerGroup) => (
                      <TableHeaderRow {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => {
                          const columnId = column.id;
                          const _column = newColumns.filter((column) => column.id === columnId);
                          const _columnWidth = _column[0].width;
                          return (
                            <TableHeaderCell
                              width={_columnWidth}
                              {...column.getHeaderProps(column.getSortByToggleProps())}
                            >
                              {column.render('Header')}
                              {column.isSorted && (
                                <Wrapper as="span">
                                  <Space minWidth="1x" />
                                  {column.isSortedDesc ? (
                                    <Icon icon="sort-down" size={20} />
                                  ) : (
                                    <Icon icon="sort-up" size={20} />
                                  )}
                                </Wrapper>
                              )}
                            </TableHeaderCell>
                          );
                        })}
                      </TableHeaderRow>
                    ))}
                  </TableHeader>
                  {/*set TableBody height by subtracting TableHeader height(40)*/}
                  <TableBody {...getTableBodyProps()} className={styles.tableBody}>
                    {rows.length === 0 && (
                      <Wrapper textAlign="center">
                        <Text color="gray:60" size="lg" mt={70}>
                          {t('noSearchResults')}
                        </Text>
                      </Wrapper>
                    )}
                    {rows.map((row) => {
                      row.isSelected = enableMultiSelect
                        ? Object.keys(selectedRowIds).includes(row.id)
                        : selectedRow && rowSelectionKey === 'roleID'
                        ? // Match on id and roleID
                          (row.original['parentAccountId'] === selectedRow['id'] &&
                            row.original[rowSelectionKey] === selectedRow[rowSelectionKey]) ||
                          (!row.canExpand &&
                            row.original['id'] === selectedRow['id'] &&
                            row.original[rowSelectionKey] === selectedRow[rowSelectionKey])
                        : row.original[rowSelectionKey] === selectedRow[rowSelectionKey];

                      const parentRowId = row.id.split('.').slice(0, -1).join('.');
                      const parentRow = instance?.rowsById[parentRowId];
                      const lastSubRow =
                        parentRow?.subRows[parentRow?.subRows?.length - 1]?.values?.roleID ===
                        row.original[rowSelectionKey];

                      prepareRow(row);
                      return (
                        <TableRow
                          _hover={{
                            bg: rowHoverBgColor,
                          }}
                          className={`${
                            lastSubRow ? styles.lastSubRow : parentRow ? styles.tableRow : ''
                          } ${colorModeClass}`}
                          {...row.getRowProps({
                            style: {
                              background: row?.isSelected ? rowSelectedBgColour : '',
                              overflowWrap: 'break-word',
                            },
                          })}
                          key={row.id}
                        >
                          {row.cells.map((cell) => {
                            const columnId = cell.column.id;
                            const _column = newColumns.filter((column) => column.id === columnId);
                            const _columnWidth = _column[0].width;
                            const _getExpandableColumn = () =>
                              cell?.column?.id === 'showExpandableRows' && cell?.row?.depth > 0;

                            return (
                              <TableCell
                                key={columnId}
                                width={_columnWidth}
                                className={
                                  _getExpandableColumn() ? styles.selectedExpandableCell : ''
                                }
                                onClick={() => columnId !== 'selection' && onCellClick(row)}
                              >
                                {cell.render('Cell')}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </>
            );
          }}
        </AutoSizer>
      </TableContainer>
    );
  }
);
export default TablePanel;
